import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { useAlert } from 'react-alert';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { stripePromise } from '../../configurable/stripe';
import CheckoutComponent from '../../components/CheckoutComponent';
import BankTransferInstructions, { BankTransferInfo } from '../../components/BankTransferInstructions';
import FormField from '../../components/FormField';
import { Pricing, productNames, formatPrice, paymentModelLabel } from '../../components/ProductCatalog';
import { isProductLocked } from '../../products';
import { readJson } from '../../apiHelper/readJson';
import { getAttribution } from '../../apiHelper/attribution';
import PromoCountdown from '../../components/PromoCountdown';
import { reportPaymentError } from '../../analytics';
import './styles/Checkout.scss';

// ExtraHours (Products enum value 3) is the only product sold by the unit — the
// buyer picks how many extra lessons to purchase; every other product is a single
// purchase. Backend clamps the same range (OrdersService.CreateOrder).
const EXTRA_HOURS = 3;
const MAX_QUANTITY = 20;

// Dedicated checkout page (replaces the cart popup): order summary + discount +
// inline registration (guests) / Stripe payment. Only the first installment is
// charged here; the entitlement is granted by the webhook on success.
export default function Checkout() {
    const { product } = useParams();
    const productId = Number(product);

    const [pricing, setPricing] = useState<Pricing[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [page, setPage] = useState(0); // 0 = summary, 1 = registration (guests only)

    const [code, setCode] = useState("");
    // The code that actually validated — only this is sent to the backend. Sending the raw
    // input instead would (a) dead-end the whole order on a typo the backend rejects, and
    // (b) let the shown price disagree with the charge when a valid code wasn't "applied".
    const [appliedCode, setAppliedCode] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [discountPercent, setDiscountPercent] = useState(0);
    const [clientSecret, setClientSecret] = useState("");
    // Payment method: 0 = card (Stripe), 1 = bank transfer / QR (paid off-platform).
    const [method, setMethod] = useState<0 | 1>(0);
    const [bankInfo, setBankInfo] = useState<BankTransferInfo | null>(null);
    const [done, setDone] = useState(false);
    const [owned, setOwned] = useState<number[]>([]);
    const [ownedChecked, setOwnedChecked] = useState(false);
    // Guest post-payment login state: "pending" while we wait for the activation
    // webhook, "done" once auto-logged-in, "failed" → fall back to manual sign-in.
    const [autoLogin, setAutoLogin] = useState<"idle" | "pending" | "done" | "failed">("idle");
    const [switchingToCard, setSwitchingToCard] = useState(false);

    const [name, setName] = useState("");
    const [surname, setSurname] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordRep, setPasswordRep] = useState("");
    const [confirmation, setConfirmation] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Billing address — only asked for when the order needs a full "daňový doklad" (a
    // single payment reaching the ≥10 000 Kč VAT-law threshold); every smaller/instalment
    // purchase qualifies for a zjednodušený daňový doklad and needs no customer address.
    // Company (IČO/DIČ) buyers are a fraction of a percent, so we don't clutter checkout
    // with a business toggle — those are handled by hand (the invoice PDF renders company
    // details straight from the profile if an admin fills them in).
    const [street, setStreet] = useState("");
    const [city, setCity] = useState("");

    const alert = useAlert();
    const navigate = useNavigate();
    const location = useLocation();
    const api = new ApiHelper();

    const loggedIn = localStorage.getItem("token") !== null;

    useEffect(() => {
        fetchPricing();
        // A signed-in buyer can't re-buy a product they already own (or that is bundled
        // in one they own) — block it before charging. Guests are covered server-side.
        if (loggedIn) fetchOwned(); else setOwnedChecked(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function fetchPricing() {
        try {
            const res = await api.pricingApi.getPricingList();
            setPricing(await res.json());
        } catch (x: any) {
            api.processErrors(x);
        } finally {
            setLoaded(true);
        }
    }

    async function fetchOwned() {
        try {
            const res = await api.userApi.getProductsList();
            const products = await readJson<{ product: number }[]>(res);
            setOwned((products ?? []).map((p) => p.product));
        } catch { /* backend still guards re-purchase */ } finally {
            setOwnedChecked(true);
        }
    }

    // Called when Stripe confirms the payment. Logged-in buyers already have a session;
    // for guests we try to auto-log-in with the account they just created so they land
    // straight in the course instead of re-typing their credentials.
    function handlePaid() {
        setDone(true);
        if (!loggedIn) autoLoginGuest();
    }

    async function autoLoginGuest() {
        setAutoLogin("pending");
        // The account is activated by the Stripe webhook, which can land a beat after the
        // client sees the payment succeed — so retry a few times, then fall back to the
        // manual sign-in prompt.
        for (let attempt = 0; attempt < 4; attempt++) {
            try {
                const res = await api.userApi.loginCreate({ username: email, password });
                const data = await res.json();
                if (data?.token) {
                    localStorage.setItem("token", "Bearer " + data.token);
                    setAutoLogin("done");
                    return;
                }
            } catch { /* not active yet — wait for the webhook and retry */ }
            if (attempt < 3) await new Promise((r) => setTimeout(r, 2500));
        }
        setAutoLogin("failed");
    }

    async function validateCode() {
        if (!code) return;

        try {
            const res = await api.discountApi.validateList({ code: code, product: productId });
            const proc = await res.json();

            if (proc.valid) {
                setAppliedCode(code);
                setDiscountPercent(proc.percent);
                alert.success("Sleva " + proc.percent + " % byla použita");
            } else {
                setAppliedCode("");
                setDiscountPercent(0);
                alert.error("Neplatný slevový kód");
            }
        } catch (x: any) {
            api.processErrors(x);
        }
    }

    // Required only when requiresBillingAddress — otherwise no billing fields are shown at
    // all, so there is nothing to validate.
    function billingAddressMissing() {
        return requiresBillingAddress && (!street.trim() || !city.trim());
    }

    function billingFields() {
        return requiresBillingAddress ? { street, city } : {};
    }

    // Shown only when requiresBillingAddress — a single OneTime payment ≥10 000 Kč needs
    // a full daňový doklad, which must identify the buyer (jméno a sídlo).
    function renderBillingFields() {
        if (!requiresBillingAddress) return null;

        return (
            <div className="form billingFields">
                <div className="formHead">Fakturační adresa</div>
                <p className="billingHint">Objednávka je nad 10 000 Kč jednorázově — pro daňový doklad potřebujeme tvou adresu.</p>
                <FormField label="ulice a číslo" value={street} handle={setStreet} />
                <FormField label="město a PSČ" value={city} handle={setCity} />
            </div>
        );
    }

    async function buyLoggedIn() {
        if (submitting) return;

        if (billingAddressMissing()) {
            alert.error("Pro platbu nad 10 000 Kč vyplň prosím fakturační adresu (ulice a město).");
            return;
        }

        setSubmitting(true);

        try {
            const res = await api.ordersApi.createCreate({ product: productId, code: appliedCode || undefined, quantity: isExtraHours ? quantity : undefined, paymentMethod: method, ...getAttribution(), ...billingFields() });
            const proc = await res.json();
            applyOrderResult(proc);
        } catch (x: any) {
            reportPaymentError("order_create_failed", x?.status);
            api.processErrors(x);
        } finally {
            setSubmitting(false);
        }
    }

    // Card orders return a Stripe client secret (→ show the payment form); bank-transfer orders
    // return the payment details + QR (→ show the transfer instructions).
    function applyOrderResult(proc: any) {
        if (method === 1) {
            setBankInfo({
                amount: proc.firstPaymentAmount,
                variableSymbol: proc.variableSymbol,
                iban: proc.iban,
                accountDisplay: proc.accountDisplay,
                qrCode: proc.qrCode,
                installmentId: proc.installmentId,
                invoiceId: proc.invoiceId,
            });
        } else {
            setClientSecret(proc.key);
        }
    }

    // "Zaplatit kartou místo toho" on a bank-transfer order: swap this same page over to the
    // card form instead of navigating to the standalone /platba/:id page. Only works right
    // after checkout (backend rejects it once a payment reminder has been sent for this
    // installment — see OrdersService.SwitchToCard).
    async function payByCardInstead() {
        if (!bankInfo?.installmentId || switchingToCard) return;
        setSwitchingToCard(true);

        try {
            const res = await api.ordersApi.switchToCardCreate(bankInfo.installmentId);
            const proc = await res.json();
            if (proc.key) {
                setBankInfo(null);
                setClientSecret(proc.key);
            }
        } catch (x: any) {
            api.processErrors(x);
        } finally {
            setSwitchingToCard(false);
        }
    }

    async function buyPublic() {
        if (submitting) return;

        if (password !== passwordRep) {
            alert.error("Hesla nejsou shodná");
            return;
        }

        if (!confirmation) {
            alert.error("Bez souhlasu se nelze registrovat");
            return;
        }

        if (billingAddressMissing()) {
            alert.error("Pro platbu nad 10 000 Kč vyplň prosím fakturační adresu (ulice a město).");
            return;
        }

        setSubmitting(true);

        try {
            const res = await api.ordersApi.createPublicCreate({
                name, surname, email, password, product: productId, code: appliedCode || undefined,
                quantity: isExtraHours ? quantity : undefined, paymentMethod: method, ...getAttribution(),
                ...billingFields(),
            });
            const proc = await res.json();
            applyOrderResult(proc);
        } catch (x: any) {
            reportPaymentError("public_order_create_failed", x?.status);
            api.processErrors(x);
        } finally {
            setSubmitting(false);
        }
    }

    function priceWithDiscount(value: number) {
        return discountPercent > 0 ? Math.round(value * (1 - discountPercent / 100)) : value;
    }

    const current = pricing.find((p) => p.product === productId);
    const installment = current ? current.paymentModel !== 0 : false;
    const isExtraHours = current?.product === EXTRA_HOURS;
    const nowAmount = current ? priceWithDiscount(current.firstPaymentAmount) : 0;
    // Only ExtraHours scales with quantity; every other product is a single unit.
    const payNow = nowAmount * (isExtraHours ? quantity : 1);
    // Block the checkout for a signed-in buyer who already owns this product (or owns
    // one that includes it) — extra hours are repeatable, so they're never locked.
    const alreadyOwned = loggedIn && isProductLocked(productId, owned);
    // Mirrors OrdersService.CreateOrder's server-side check — a single OneTime payment
    // ≥10 000 Kč needs a full daňový doklad, which must identify the buyer.
    const requiresBillingAddress = !!current && current.paymentModel === 0 && payNow >= 10000;

    if (loaded && !current) {
        return (
            <div className="checkout">
                <div className="checkoutInner">
                    <Link to="/nakup" className="backLink">← Zpět na nabídku</Link>
                    <div className="missing">Produkt nenalezen.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="checkout">
            <div className="checkoutInner">
                <Link to="/nakup" className="backLink">← Zpět na nabídku</Link>
                <h1 className="title">Pokladna</h1>

                {!current || (loggedIn && !ownedChecked) ?
                    <div className="missing">Načítání…</div>
                    : done ?
                    <div className="doneCard">
                        <div className="check">✓</div>
                        <h2>Platba proběhla</h2>
                        {loggedIn || autoLogin === "done" ?
                            <>
                                <p>Produkt je aktivní. Hodně štěstí při přípravě!</p>
                                <button type="button" className="pay" onClick={() => navigate("/kurz")}>Přejít do kurzu</button>
                            </>
                            : autoLogin === "pending" ?
                            <>
                                <p>Aktivujeme tvůj účet a přihlašujeme tě…</p>
                                <div className="pay disabled">Přihlašování…</div>
                            </>
                            :
                            <>
                                <p>Tvůj účet je teď aktivní. Přihlas se a pusť se do přípravy.</p>
                                <Link to="/prihlaseni" state={{ from: "/kurz" }} className="pay">Přihlásit se</Link>
                            </>
                        }
                    </div>
                    : alreadyOwned ?
                    <div className="doneCard">
                        <div className="check">✓</div>
                        <h2>Tento produkt už máš</h2>
                        <p>K tomuto produktu už máš přístup, takže ho není potřeba kupovat znovu.</p>
                        <button type="button" className="pay" onClick={() => navigate("/kurz")}>Přejít do kurzu</button>
                    </div>
                    : !current.available ?
                    <div className="doneCard">
                        <h2>Nyní nelze zakoupit</h2>
                        <p>Tento produkt momentálně není v prodeji. Vyber si prosím jiný plán.</p>
                        <button type="button" className="pay" onClick={() => navigate("/nakup")}>Zpět na nabídku</button>
                    </div>
                    :
                    <div className="cols">
                        <aside className="summaryCol">
                            <div className="summary">
                                <div className="prodRow">
                                    <div className="pInfo">
                                        <h3>{productNames[current.product]}</h3>
                                        <span className="pModel">{paymentModelLabel(current)}</span>
                                    </div>
                                </div>

                                <div className="lines">
                                    <div className="row">
                                        <span>{current.paymentModel === 2 ? "Cena za měsíc" : isExtraHours ? "Cena za lekci" : "Cena"}</span>
                                        <span>
                                            {current.discountedPrice != null &&
                                                <span className="anchor">{formatPrice(current.basePrice)}</span>
                                            }
                                            {formatPrice(current.effectivePrice)}{current.paymentModel === 2 ? " / měs." : ""}
                                        </span>
                                    </div>

                                    {current.paymentModel === 2 && current.monthCount != null &&
                                        <div className="row muted"><span>Počet měsíčních splátek</span><span>{current.monthCount}×</span></div>
                                    }

                                    {isExtraHours &&
                                        <div className="row qtyRow">
                                            <span>Počet lekcí</span>
                                            {clientSecret === "" ?
                                                <span className="stepper">
                                                    <button type="button" aria-label="Ubrat lekci" disabled={quantity <= 1}
                                                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}>−</button>
                                                    <b>{quantity}</b>
                                                    <button type="button" aria-label="Přidat lekci" disabled={quantity >= MAX_QUANTITY}
                                                        onClick={() => setQuantity((q) => Math.min(MAX_QUANTITY, q + 1))}>+</button>
                                                </span>
                                                :
                                                <span>{quantity}×</span>
                                            }
                                        </div>
                                    }

                                    {discountPercent > 0 &&
                                        <div className="row muted"><span>Slevový kód</span><span>−{discountPercent} %</span></div>
                                    }

                                    <div className="row total">
                                        <span>{installment ? "První platba nyní" : "K úhradě"}</span>
                                        <span>{formatPrice(payNow)}</span>
                                    </div>
                                </div>

                                {current.paymentModel === 1 &&
                                    <p className="note">
                                        Druhá platba {formatPrice(priceWithDiscount(current.effectivePrice - current.firstPaymentAmount))}
                                        {current.secondPartDueDays != null ? " se splatností za " + current.secondPartDueDays + " dní" : ""} — včas ti ji připomeneme.
                                    </p>
                                }
                                {current.paymentModel === 2 &&
                                    <p className="note">Další měsíční platby {formatPrice(nowAmount)} ti automaticky připomeneme e-mailem i v aplikaci.</p>
                                }

                                {current.discountValidUntil &&
                                    <div className="promoRow">
                                        <PromoCountdown deadline={current.discountValidUntil} />
                                    </div>
                                }
                            </div>
                        </aside>

                        <section className="payCol">
                            {bankInfo ?
                                <div className="doneCard">
                                    <div className="check">✓</div>
                                    <h2>Objednávka vytvořena</h2>
                                    <p>Zaplať převodem podle údajů níže. Jakmile platbu obdržíme, aktivujeme ti přístup — pokyny jsme ti poslali i e-mailem.</p>
                                    <BankTransferInstructions info={bankInfo} canDownload={loggedIn} onPayByCard={payByCardInstead} payingByCard={switchingToCard} />
                                    {loggedIn ?
                                        <button type="button" className="pay" onClick={() => navigate("/kurz")}>Přejít do kurzu</button>
                                        :
                                        <p className="signinHint">Po přihlášení uvidíš stav objednávky. <Link to="/prihlaseni" state={{ from: "/kurz" }}>Přihlásit se</Link></p>
                                    }
                                </div>
                                : clientSecret !== "" ?
                                <Elements stripe={stripePromise} options={{ clientSecret: clientSecret }}>
                                    <CheckoutComponent clientSecret={clientSecret} onSuccess={handlePaid} />
                                </Elements>
                                :
                                <>
                                    <div className="methodRow">
                                        <button type="button" className={"methodOpt" + (method === 0 ? " active" : "")} onClick={() => setMethod(0)}>Kartou</button>
                                        <button type="button" className={"methodOpt" + (method === 1 ? " active" : "")} onClick={() => setMethod(1)}>Převodem / QR</button>
                                    </div>
                                    <div className="codeRow">
                                        <input
                                            placeholder="Slevový kód"
                                            value={code}
                                            onChange={(e) => { setCode(e.currentTarget.value); setAppliedCode(""); setDiscountPercent(0); }}
                                        />
                                        <button className="codeButton" onClick={() => validateCode()}>Použít</button>
                                    </div>

                                    {loggedIn ?
                                        <>
                                            {renderBillingFields()}
                                            <button type="button" className={"pay" + (submitting ? " disabled" : "")} disabled={submitting} onClick={() => buyLoggedIn()}>{method === 1 ? "Zaplatit převodem" : "Zaplatit " + formatPrice(payNow)}</button>
                                        </>
                                        : page === 0 ?
                                        <>
                                            <button type="button" className="pay" onClick={() => setPage(1)}>Pokračovat</button>
                                            <p className="signinHint">Už máš účet? <Link to="/prihlaseni" state={{ from: location.pathname }}>Přihlas se</Link></p>
                                        </>
                                        :
                                        <div className="form">
                                            <div className="formHead">Vytvoř si účet</div>
                                            <FormField label="jméno" value={name} handle={setName} />
                                            <FormField label="příjmení" value={surname} handle={setSurname} />
                                            <FormField label="email" value={email} handle={setEmail} />
                                            <FormField label="heslo" value={password} handle={setPassword} password={true} />
                                            <FormField label="heslo znovu" value={passwordRep} handle={setPasswordRep} password={true} />

                                            {renderBillingFields()}

                                            <label className="conf">
                                                <input type="checkbox" checked={confirmation} onChange={() => setConfirmation((c) => !c)} />
                                                <span>Souhlasím s obchodními podmínkami a podmínkami GDPR.</span>
                                            </label>

                                            <button type="button" className={"pay" + (submitting ? " disabled" : "")} disabled={submitting} onClick={() => buyPublic()}>{method === 1 ? "Zaplatit převodem" : "Zaplatit " + formatPrice(payNow)}</button>
                                        </div>
                                    }
                                </>
                            }
                        </section>
                    </div>
                }
            </div>
        </div>
    );
}
